/**
 * Contents — bridge to an iframe document.
 *
 * Provides CFI ↔ Range conversion, CSS manipulation, dimension measurement,
 * and event forwarding from the iframe to the rendition.
 */

import { Emitter } from "./emitter";
import EpubCFI from "../cfi/cfi";
import { measureRenderableContentExtent } from "../../utils/paginatedViewport";

// Forward these DOM events from iframe to rendition
const DOM_EVENTS = [
  "keydown",
  "keyup",
  "keypressed",
  "mouseup",
  "mousedown",
  "mousemove",
  "click",
  "touchstart",
  "touchmove",
  "touchend",
  "touchcancel",
] as const;

const SCROLLED_DEFAULT_TOP_PADDING_PX = 48;
const SCROLLED_DEFAULT_BOTTOM_PADDING_PX = 120;
const READER_VIEWPORT_HEIGHT_VAR = "--wr-reader-viewport-height";
const FULL_HEIGHT_IMAGE_LAYOUT_STYLE_KEY = "epubjs-full-height-image-layout";

const FULL_HEIGHT_IMAGE_PAGE_SELECTORS = [
  "body.__FLOW__ > .height_100:only-child",
  "body.__FLOW__ > :only-child > .height_100:only-child",
  "body.__FLOW__ > #epub-content-wrapper > .height_100:only-child",
  "body.__FLOW__ > #epub-content-wrapper > :only-child > .height_100:only-child",
];

const FULL_HEIGHT_IMAGE_SELECTORS = FULL_HEIGHT_IMAGE_PAGE_SELECTORS.map(
  (selector) => `${selector} > img.fill_height`,
);

const renderFullHeightSelectors = (flowClass: string, selectors: string[]) =>
  selectors
    .map((selector) => selector.replace(/__FLOW__/g, flowClass))
    .join(",\n");

const FULL_HEIGHT_IMAGE_LAYOUT_CSS = `
${renderFullHeightSelectors("paginated-flow", FULL_HEIGHT_IMAGE_PAGE_SELECTORS)} {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
  height: calc(var(${READER_VIEWPORT_HEIGHT_VAR}, 100vh) - var(--wr-paginated-top-padding, 48px) - var(--wr-paginated-bottom-padding, 48px)) !important;
  max-height: calc(var(${READER_VIEWPORT_HEIGHT_VAR}, 100vh) - var(--wr-paginated-top-padding, 48px) - var(--wr-paginated-bottom-padding, 48px)) !important;
}

${renderFullHeightSelectors("scroll-flow", FULL_HEIGHT_IMAGE_PAGE_SELECTORS)} {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
  height: calc(var(${READER_VIEWPORT_HEIGHT_VAR}, 100vh) - var(--wr-scrolled-top-padding, 48px) - var(--wr-scrolled-bottom-padding, 120px)) !important;
  max-height: calc(var(${READER_VIEWPORT_HEIGHT_VAR}, 100vh) - var(--wr-scrolled-top-padding, 48px) - var(--wr-scrolled-bottom-padding, 120px)) !important;
}

${renderFullHeightSelectors("paginated-flow", FULL_HEIGHT_IMAGE_SELECTORS)},
${renderFullHeightSelectors("scroll-flow", FULL_HEIGHT_IMAGE_SELECTORS)} {
  width: 100% !important;
  height: 100% !important;
  max-width: 100% !important;
  max-height: 100% !important;
  object-fit: contain !important;
  margin: 0 auto !important;
}
`;

type SizeOptions = {
  viewportHeight?: number | null;
};

export class Contents extends Emitter {
  document: Document;
  documentElement: HTMLElement;
  content: HTMLElement;
  window: Window;
  cfiBase: string;
  sectionIndex: number;

  private _resizeObserver: ResizeObserver | null = null;
  private _selectionTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    doc: Document,
    body: HTMLElement,
    cfiBase: string,
    sectionIndex: number,
  ) {
    super();
    this.document = doc;
    this.documentElement = doc.documentElement;
    this.content = body;
    this.window = doc.defaultView!;
    this.cfiBase = cfiBase;
    this.sectionIndex = sectionIndex;
  }

  // ---- CFI ↔ Range ----

  cfiFromRange(range: Range, ignoreClass?: string): string {
    return new EpubCFI(
      range,
      this.cfiBase,
      ignoreClass ?? "epubjs-ignore",
    ).toString();
  }

  range(cfi: string, ignoreClass?: string): Range | null {
    return new EpubCFI(cfi).toRange(this.document, ignoreClass);
  }

  // ---- Dimension measurement ----

  width(): number {
    return this.content.clientWidth;
  }

  height(): number {
    return this.content.clientHeight;
  }

  textWidth(): number {
    const r = this.document.createRange();
    r.selectNodeContents(this.content);
    const rect = r.getBoundingClientRect();
    return rect.width;
  }

  textHeight(): number {
    const r = this.document.createRange();
    r.selectNodeContents(this.content);
    const rect = r.getBoundingClientRect();
    return rect.height;
  }

  scrolledHeight(): number {
    const r = this.document.createRange();
    r.selectNodeContents(this.content);

    let rangeBottom = 0;
    try {
      const rangeRect = r.getBoundingClientRect();
      const contentRect = this.content.getBoundingClientRect();
      const computed = this.window.getComputedStyle(this.content);
      const paddingBottom = parseFloat(computed.paddingBottom || "0") || 0;
      if (rangeRect.width > 0 || rangeRect.height > 0) {
        rangeBottom = Math.max(
          rangeRect.bottom - contentRect.top + paddingBottom,
          0,
        );
      }
    } catch (_error) {
      rangeBottom = 0;
    }

    return Math.max(this.content.scrollHeight, rangeBottom);
  }

  scrollWidth(): number {
    // Use body.scrollWidth, not documentElement.scrollWidth.
    // The html element inherits the iframe viewport width, so once expand()
    // sizes the iframe wider during an intermediate reflow,
    // html.scrollWidth gets stuck at that larger value
    // even after styles shrink the actual content — causing blank trailing
    // pages.  The body has a fixed CSS width (448px / viewport), so its
    // scrollWidth accurately reflects the column-overflow content width.
    return this.content.scrollWidth;
  }

  renderedContentWidth(): number {
    return measureRenderableContentExtent(this.document, "horizontal");
  }

  scrollHeight(): number {
    return Math.max(
      this.documentElement.scrollHeight,
      this.content.scrollHeight,
    );
  }

  // ---- CSS manipulation ----

  css(property: string, value?: string, priority?: string): void {
    if (value) {
      this.content.style.setProperty(property, value, priority);
    } else {
      this.content.style.removeProperty(property);
    }
  }

  overflow(value: string): void {
    this.documentElement.style.overflow = value;
  }

  overflowX(value: string): void {
    this.documentElement.style.overflowX = value;
  }

  overflowY(value: string): void {
    this.documentElement.style.overflowY = value;
  }

  direction(dir: string): void {
    this.documentElement.dir = dir;
  }

  writingMode(): string {
    const val =
      this.content.style.writingMode ||
      getComputedStyle(this.content).writingMode;
    return val || "";
  }

  private syncFlowSizing(
    flowClass: "paginated-flow" | "scroll-flow",
    viewportHeight?: number | null,
  ): void {
    this.content.classList.toggle(
      "paginated-flow",
      flowClass === "paginated-flow",
    );
    this.content.classList.toggle("scroll-flow", flowClass === "scroll-flow");
    if (
      viewportHeight != null &&
      Number.isFinite(viewportHeight) &&
      viewportHeight > 0
    ) {
      this.content.style.setProperty(
        READER_VIEWPORT_HEIGHT_VAR,
        `${viewportHeight}px`,
      );
    }
    this.addStylesheetCss(
      FULL_HEIGHT_IMAGE_LAYOUT_CSS,
      FULL_HEIGHT_IMAGE_LAYOUT_STYLE_KEY,
    );
  }

  /** Apply multi-column pagination CSS. */
  columns(
    width: number,
    height: number,
    columnWidth: number,
    gap: number,
    dir?: string,
  ): void {
    const s = this.content.style;
    const ds = this.documentElement.style;
    this.syncFlowSizing("paginated-flow", height);

    // Body — match epub.js approach: only hide overflow-y,
    // leave overflow-x default so documentElement.scrollWidth captures columns.
    s.overflowY = "hidden";
    s.width = width + "px";
    s.height = height + "px";

    // Keep margin authoritative, but let reader CSS own horizontal padding.
    // That allows two-page spreads to split the requested margin between
    // body and wrapper padding so the center gutter matches the outer margins.
    s.setProperty("margin", "0", "important");
    s.setProperty("padding-top", "48px");
    s.setProperty("padding-bottom", "48px");
    s.setProperty("padding-left", gap / 2 + "px");
    s.setProperty("padding-right", gap / 2 + "px");
    s.boxSizing = "border-box";
    s.setProperty("column-width", columnWidth + "px");
    s.setProperty("column-gap", gap + "px");
    s.setProperty("column-fill", "auto");
    s.maxWidth = "inherit";

    // Root element — reset margin/padding and set height for column containment.
    // Do NOT set width or overflow on html; that would cap scrollWidth.
    ds.setProperty("margin", "0", "important");
    ds.setProperty("padding", "0", "important");
    ds.height = height + "px";

    if (dir === "rtl") this.direction("rtl");
  }

  /** Apply sizing for scrolled mode. */
  size(
    width: number | null,
    height: number | null,
    options: SizeOptions = {},
  ): void {
    const s = this.content.style;
    const ds = this.documentElement.style;
    const hGap = Math.round((width ?? this.content.clientWidth) / 12);
    this.syncFlowSizing("scroll-flow", options.viewportHeight ?? height);

    // Preserve reader-owned vertical spacing so scrolled chapters retain
    // their native safe-area runway at the start/end of the section.
    s.overflowY = "";
    s.overflowX = "";
    s.setProperty(
      "padding-top",
      `var(--wr-scrolled-top-padding, ${SCROLLED_DEFAULT_TOP_PADDING_PX}px)`,
    );
    s.setProperty(
      "padding-bottom",
      `var(--wr-scrolled-bottom-padding, ${SCROLLED_DEFAULT_BOTTOM_PADDING_PX}px)`,
    );
    s.setProperty("padding-left", `${hGap}px`);
    s.setProperty("padding-right", `${hGap}px`);
    s.boxSizing = "border-box";
    s.setProperty("margin", "0", "important");
    s.maxWidth = "";
    s.removeProperty("column-width");
    s.removeProperty("column-gap");
    s.removeProperty("column-fill");
    if (width != null) {
      s.width = width + "px";
    } else {
      s.width = "";
    }
    if (height != null) {
      s.height = height + "px";
    } else {
      s.height = "";
    }

    ds.height = "";
    ds.overflow = "";
    ds.overflowX = "";
    ds.overflowY = "";

    console.debug("[epub:contents] size scrolled reset", {
      sectionIndex: this.sectionIndex,
      width,
      height,
      body: {
        width: s.width || null,
        height: s.height || null,
        paddingTop: s.getPropertyValue("padding-top") || null,
        paddingBottom: s.getPropertyValue("padding-bottom") || null,
        paddingLeft: s.getPropertyValue("padding-left") || null,
        paddingRight: s.getPropertyValue("padding-right") || null,
        columnWidth: s.getPropertyValue("column-width") || null,
        columnGap: s.getPropertyValue("column-gap") || null,
        columnFill: s.getPropertyValue("column-fill") || null,
        overflowX: s.overflowX || null,
        overflowY: s.overflowY || null,
      },
      html: {
        height: ds.height || null,
        overflow: ds.overflow || null,
        overflowX: ds.overflowX || null,
        overflowY: ds.overflowY || null,
      },
    });
  }

  /** Apply scaling for pre-paginated (fixed-layout) content. */
  fit(width: number, height: number, _section: unknown): void {
    const vpEl = this.document.querySelector("meta[name='viewport']");
    let vpWidth = width;
    let vpHeight = height;

    if (vpEl) {
      const content = vpEl.getAttribute("content") || "";
      const wMatch = content.match(/width=(\d+)/);
      const hMatch = content.match(/height=(\d+)/);
      if (wMatch) vpWidth = parseInt(wMatch[1], 10);
      if (hMatch) vpHeight = parseInt(hMatch[1], 10);
    }

    const scaleX = width / vpWidth;
    const scaleY = height / vpHeight;
    const scale = Math.min(scaleX, scaleY);

    const s = this.documentElement.style;
    s.fontSize = "0"; // remove any inherited
    s.overflow = "hidden";
    s.width = vpWidth + "px";
    s.height = vpHeight + "px";
    s.transformOrigin = "top left";
    s.transform = `scale(${scale})`;
  }

  // ---- Stylesheet injection ----

  addStylesheet(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const link = this.document.createElement("link");
      link.rel = "stylesheet";
      link.href = src;
      link.onload = () => resolve();
      link.onerror = reject;
      this.document.head.appendChild(link);
    });
  }

  addStylesheetCss(css: string, key?: string): void {
    const id = key ? `epubjs-inserted-css-${key}` : undefined;
    if (id) {
      const existing = this.document.getElementById(id);
      if (existing) {
        existing.textContent = css;
        return;
      }
    }
    const style = this.document.createElement("style");
    if (id) style.id = id;
    style.textContent = css;
    this.document.head.appendChild(style);
  }

  addStylesheetRules(
    rules: Record<string, Record<string, string>> | string[][],
    key?: string,
  ): void {
    let css = "";
    if (Array.isArray(rules)) {
      for (const rule of rules) {
        css += rule[0] + " { " + rule[1] + " }\n";
      }
    } else {
      for (const selector of Object.keys(rules)) {
        css += selector + " {\n";
        for (const [prop, val] of Object.entries(rules[selector])) {
          // Convert camelCase property names (e.g. paddingTop) to kebab-case
          // (padding-top) for valid CSS output.
          const kebab = prop.replace(/([A-Z])/g, "-$1").toLowerCase();
          css += `  ${kebab}: ${val};\n`;
        }
        css += "}\n";
      }
    }
    this.addStylesheetCss(css, key);
  }

  addClass(className: string): void {
    this.content.classList.add(className);
  }

  removeClass(className: string): void {
    this.content.classList.remove(className);
  }

  // ---- Event listeners ----

  listeners(): void {
    this.addEventListeners();
    this.addSelectionListeners();
    this.addResizeObserver();
    this.imageLoadListeners();
    this.linksHandler();
  }

  private addEventListeners(): void {
    for (const event of DOM_EVENTS) {
      this.document.addEventListener(event, (e) => this.emit(event, e), {
        // A held translation swipe must be able to prevent native scrolling.
        passive: event !== "touchmove",
      });
    }
  }

  private addSelectionListeners(): void {
    this.document.addEventListener("selectionchange", () => {
      if (this._selectionTimeout) clearTimeout(this._selectionTimeout);
      this._selectionTimeout = setTimeout(() => {
        const sel = this.window.getSelection();
        if (!sel || sel.isCollapsed) return;
        const range = sel.getRangeAt(0);
        const cfi = this.cfiFromRange(range);
        this.emit("selected", cfi, range);
      }, 250);
    });
  }

  private addResizeObserver(): void {
    this._resizeObserver = new ResizeObserver(() => {
      this.emit("resize");
    });
    this._resizeObserver.observe(this.content);
  }

  private imageLoadListeners(): void {
    const imgs = this.document.querySelectorAll("img");
    for (const img of imgs) {
      if (!img.complete) {
        img.addEventListener("load", () => this.emit("expand"), { once: true });
      }
    }
  }

  private linksHandler(): void {
    const links = this.document.querySelectorAll("a[href]");
    for (const link of links) {
      link.addEventListener("click", (e) => {
        const href = (link as HTMLAnchorElement).getAttribute("href");
        if (!href) return;
        e.preventDefault();
        e.stopPropagation();
        this.emit("linkClicked", href);
      });
    }
  }

  /** Resolve a CFI or fragment to pixel coordinates (document-relative). */
  locationOf(
    target: string,
    ignoreClass?: string,
  ): {
    left: number;
    top: number;
    found: boolean;
    resolution:
      | "range-rect"
      | "range-parent-rect"
      | "range-offset"
      | "hash-rect"
      | "hash-offset"
      | "unresolved";
  } {
    if (target.startsWith("epubcfi(")) {
      const r = this.range(target, ignoreClass);
      console.debug("[epub:contents] locationOf", {
        target: target.slice(0, 80),
        rangeFound: !!r,
        startNode: r?.startContainer?.nodeName,
        startOffset: r?.startOffset,
      });
      if (r) {
        // Primary: getBoundingClientRect on the Range
        let rect = r.getBoundingClientRect();
        // Webkit fix: collapsed ranges at column boundaries can return 0-size rect
        if (rect.width === 0 && rect.height === 0) {
          const rects = r.getClientRects();
          if (rects.length > 0) rect = rects[0];
        }
        console.debug("[epub:contents] locationOf rect", {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        });
        if (rect.left !== 0 || rect.top !== 0) {
          return {
            left: rect.left,
            top: rect.top,
            found: true,
            resolution: "range-rect",
          };
        }

        // Fallback 1: try the start container's parent element rect
        const parentEl =
          r.startContainer.nodeType === Node.ELEMENT_NODE
            ? (r.startContainer as HTMLElement)
            : r.startContainer.parentElement;
        if (parentEl) {
          const elRect = parentEl.getBoundingClientRect();
          console.debug("[epub:contents] locationOf parentEl rect fallback", {
            tag: parentEl.tagName,
            left: elRect.left,
            top: elRect.top,
          });
          if (elRect.left !== 0 || elRect.top !== 0) {
            return {
              left: elRect.left,
              top: elRect.top,
              found: true,
              resolution: "range-parent-rect",
            };
          }
        }

        // Fallback 2: walk the offsetTop/offsetParent chain for a
        // document-relative position that doesn't depend on viewport metrics
        const offset = this._offsetFromBody(r.startContainer);
        console.debug("[epub:contents] locationOf offsetTop fallback", offset);
        if (offset.top > 0 || offset.left > 0) {
          return {
            ...offset,
            found: true,
            resolution: "range-offset",
          };
        }
      }
    } else if (target.startsWith("#")) {
      const el = this.document.getElementById(target.slice(1));
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.left !== 0 || rect.top !== 0) {
          return {
            left: rect.left,
            top: rect.top,
            found: true,
            resolution: "hash-rect",
          };
        }
        const offset = this._offsetFromBody(el);
        if (offset.top > 0 || offset.left > 0) {
          return {
            ...offset,
            found: true,
            resolution: "hash-offset",
          };
        }
      }
    }

    return {
      left: 0,
      top: 0,
      found: false,
      resolution: "unresolved",
    };
  }

  /**
   * Walk the offsetParent chain to compute a document-relative position.
   * Works even when getBoundingClientRect returns {0, 0} (e.g. iframe
   * viewport rendering issues on reused views).
   */
  private _offsetFromBody(node: Node): { left: number; top: number } {
    let el: HTMLElement | null =
      node.nodeType === Node.ELEMENT_NODE
        ? (node as HTMLElement)
        : node.parentElement;
    let top = 0;
    let left = 0;
    while (el && el !== this.content && el !== this.documentElement) {
      top += el.offsetTop;
      left += el.offsetLeft;
      el = el.offsetParent as HTMLElement | null;
    }
    return { top, left };
  }

  destroy(): void {
    this._resizeObserver?.disconnect();
    if (this._selectionTimeout) clearTimeout(this._selectionTimeout);
    this.removeAllListeners();
  }
}
